import { estimatesApi } from "@/lib/api/estimates"
import type { QEPhoto } from "./types"
import {
  DAMAGE_TYPE_LABEL,
  SEVERITY_LABEL,
  type DamageTag,
} from "./damage-tag/panels"

// Quick Estimate 3 — submit pipeline.
//
// Same 3-call chain as QE2 (startFull → addImages → runDamageEstimation
// quickEstimateMode), but operates on QEPhoto[] and respects:
//   • per-photo description — sent as images[i].description (the BE
//     supports it; QE2 only used [0])
//   • sendWithoutAiTag — submitted with side FRONT_CENTER fallback
//     and qualified=false so the BE can deprioritize the agent run
//   • the "no usable images" floor — hard-block at the UI; this
//     pipeline assumes ≥1 submittable photo when called

export interface QuickEstimateSubmitArgs {
  teamId: number | string
  vin: string
  vehicle: {
    year: string | null
    make: string | null
    model: string | null
    trim: string | null
  } | null
  photos: QEPhoto[]
  /** Pre-photo damage tags from the 3D tagger. Encoded into the first
   *  image's description so the damage LLM gets structured panel + side
   *  + severity + type context up front. */
  damageTags?: DamageTag[]
  notes: string
  customer: {
    name: string
    phone: string
  }
}

// "Front bumper (driver) — moderate dent; Hood — minor scratch"
function describeDamageTags(tags: DamageTag[]): string {
  if (tags.length === 0) return ""
  const parts = tags.map(
    (t) =>
      `${t.label} — ${SEVERITY_LABEL[t.severity].toLowerCase()} ${DAMAGE_TYPE_LABEL[
        t.damageType
      ].toLowerCase()}`,
  )
  return `Reported damage: ${parts.join("; ")}`
}

export interface QuickEstimateSubmitResult {
  estimateId: number
  jobId: string | null
  /** How many of the input photos were actually attached to the BE.
   *  The success takeover surfaces this so the advisor knows. */
  photosAttached: number
}

export async function submitQuickEstimate(
  args: QuickEstimateSubmitArgs,
): Promise<QuickEstimateSubmitResult> {
  // 1. Create the estimate shell. Customer name is required by the
  // BE; service line often skips it (DMS owns it), so fall back to
  // "—". Notes go on the first attached image; if there are no
  // images the notes are lost (the BE has no top-level estimate
  // description). The UI hard-blocks the no-photo case.
  const estimate = await estimatesApi.startFull(args.teamId, {
    customer: {
      name: args.customer.name.trim() || "—",
      ...(args.customer.phone.trim() && { phone: args.customer.phone.trim() }),
    },
    vehicle: {
      vin: args.vin,
      ...(args.vehicle?.year && { year: args.vehicle.year }),
      ...(args.vehicle?.make && { make: args.vehicle.make }),
      ...(args.vehicle?.model && { model: args.vehicle.model }),
      ...(args.vehicle?.trim && { trim: args.vehicle.trim }),
    },
  })

  // 2. Attach images. Filter:
  //   • photos with a CDN URL (uploadedUrl), OR
  //   • photos the user explicitly opted to send-without-AI-tag
  // Photos with neither (in-flight at submit time, or
  // never-evaluated and not opted-in) are dropped — the UI should
  // never let this happen, but defend at the boundary.
  // BE DTO: imageUrl required URL + description required non-empty
  // string. Drop photos without an uploadedUrl — the UI should hard-
  // block this, but defend at the boundary.
  const submittable = args.photos.filter((p) => p.uploadedUrl.length > 0)
  // Structured damage context goes on the first image so the LLM reads
  // the advisor's pre-tagged panels/severity before analyzing pixels.
  const damageSummary = describeDamageTags(args.damageTags ?? [])
  let photosAttached = 0
  if (submittable.length > 0) {
    const images = submittable.map((p, idx) => {
      const perPhoto = p.description?.trim()
      // Compose a non-empty description. First row leads with the
      // structured damage tags + global notes; every row falls back to
      // a per-photo note → side-derived label. Sending undefined would
      // fail BE @IsString @IsNotEmpty validation and silently drop the
      // entire addImages call.
      const fallback = `${p.detectedSide.toLowerCase().replace(/_/g, " ")} view`
      const firstRowLead = [damageSummary, args.notes.trim()]
        .filter(Boolean)
        .join(" · ")
      const description =
        perPhoto && perPhoto.length > 0
          ? perPhoto
          : idx === 0 && firstRowLead.length > 0
            ? firstRowLead
            : fallback

      return {
        imageUrl: p.uploadedUrl,
        side: p.detectedSide,
        qualityScore: clampQuality(p.qualityScore),
        qualified: p.qualified,
        description,
      }
    })
    try {
      await estimatesApi.addImages(estimate.id, { images })
      photosAttached = images.length
    } catch (err) {
      // QE is a fire-and-forget flow and the estimate already exists,
      // so don't crash the success screen — but log the actual error
      // (not just the message) for diagnosis.
      console.warn("[quick-estimate] addImages failed:", err)
    }
  }

  // 3. Kick off the AI damage chain. quickEstimateMode chains
  // damage → repair → cost without intermediate user input.
  let jobId: string | null = null
  try {
    const job = await estimatesApi.runDamageEstimation(estimate.id, {
      quickEstimateMode: true,
    })
    jobId = job.jobId ?? null
  } catch (err) {
    console.warn("[quick-estimate] damage agent failed:", err)
  }

  return {
    estimateId: Number(estimate.id),
    jobId,
    photosAttached,
  }
}

function clampQuality(n: number | null | undefined): number {
  // BE DTO requires int 0..100. Photo evaluation may return floats or
  // out-of-range values; clamp + round to keep the call valid.
  if (n == null || Number.isNaN(n)) return 0
  return Math.max(0, Math.min(100, Math.round(n)))
}
